<template>
  <v-card
    class="rounded-lg fill-height"
    height="500"
  >
    <v-card-title class="pa-6">
      <v-row
        dense
        align="center"
      >
        <v-col class="text-no-wrap">
          <div class="subtitle-2 grey--text text--grey-100">
            {{ $t(`diagnostic_groups.${capability.capability_id}`) }}
          </div>
        </v-col>
        <v-col cols="auto">
          <v-select
            v-model="selectedVariables"
            solo
            multiple
            return-object
            :items="filteredVariables"
            dense
            item-value="var_id"
            item-text="var_name"
            :label="$t('dropdown.select_variables_to_show.title')"
            hide-details
            style="width: 250px"
          >
            <template v-slot:selection="{ item, index }">
              <span
                v-if="index === 0"
                class="text-grey text-caption align-self-center"
              >
                {{ $tc("dropdown.number_of_selected_variables", selectedVariables.length, {num: selectedVariables.length}) }}
              </span>
            </template>
          </v-select>
        </v-col>
        <v-col
          cols="auto"
          class="ml-3"
        >
          <v-btn-toggle
            v-model="view"
            color="primary"
            mandatory
          >
            <v-btn
              small
              class="text-none"
              value="visual"
              :disabled="filteredVariables.length < 3 || selectedVariables.length < 3"
            >
              {{ $t('card.display_option.visual') }}
            </v-btn>
            <v-btn
              small
              class="text-none"
              value="list"
            >
              {{ $t('card.display_option.list') }}
            </v-btn>
          </v-btn-toggle>
        </v-col>
      </v-row>
    </v-card-title>

    <v-card-text>
      <v-row>
        <v-col v-if="view === 'visual'">
          <v-row
            align="center"
            no-gutters
          >
            <v-col :cols="capability.avg_norm_comp_value ? 9 : 12">
              <div>
                <div style="max-height: 350px">
                  <radar-chart
                    v-if="activeChart === 'spider'"
                    :series="[ { name: 'value', data: selectedVariables.map(v => getValueForVariable(v.var_id, 'z_score'))} ]"
                    :tooltip="{x: {formatter: (var_id) => selectedVariables.find(v => v.var_id === var_id).var_name}, y: {title: {formatter: () => $t('tooltip.value')} }}"
                    :xaxis="{ categories: selectedVariables.map(v => v.var_id)}"
                    :yaxis="{labels: {offsetX: -13}, min: -3, max: 3}"
                    class="elevation-0 pa-0 ma-0"
                    content-class="pa-0"
                    height="350"
                  />
                  <line-chart
                    v-else
                    :raw-data="selectedVariables"
                    :chart-value-keys="[{ key: 'z_score', label: 'z score', type: 'bar' }]"
                    :xaxis="{ categories: selectedVariables.map(v => v.var_id)}"
                    :yaxis="[{ min: -3, max: 3 }]"
                    class="elevation-0 pa-0 ma-0"
                    :height="325"
                    :fill="{ type: 'solid', opacity: [0.5] }"
                    :tooltip="{x: {formatter: (index) => selectedVariables[index - 1].var_name}, y: {title: {formatter: () => $t('tooltip.value')} }}"
                  />
                </div>

                <div class="d-flex justify-center">
                  <v-btn
                    v-for="(dot, index) in ['spider', 'bar']"
                    :key="index"
                    :color="activeChart === dot ? 'primary' : 'grey'"
                    class="elevation-1 mx-1"
                    x-small
                    fab
                    outlined
                    @click="activeChart = dot"
                  >
                    <v-icon>
                      {{ dot === 'spider' ? 'fal fa-chart-radar' : 'fal fa-chart-simple' }}
                    </v-icon>
                  </v-btn>
                </div>
              </div>
            </v-col>
            <v-col
              v-if="capability.avg_norm_comp_value"
              cols="3"
            >
              <v-progress-circular
                :value="capability.avg_norm_comp_value"
                :size="125"
                :color="getColorFromValue(capability.avg_norm_comp_value, 100)"
                :width="15"
                :rotate="-90"
              >
                <div class="text-h6 font-weight-bold">
                  {{ capability.avg_norm_comp_value }}%
                </div>
              </v-progress-circular>
            </v-col>
          </v-row>
        </v-col>
        <v-col v-else>
          <div
            v-if="capability.avg_norm_comp_value"
            class="mx-10"
          >
            <v-progress-linear
              :color="getColorFromValue(capability.avg_norm_comp_value, 100)"
              background-opacity="0.2"
              height="30px"
              :value="capability.avg_norm_comp_value"
              rounded
            >
              <slot> {{ capability.avg_norm_comp_value }}% </slot>
            </v-progress-linear>
          </div>

          <v-simple-table
            dense
            fixed-header
            height="300px"
            class="ml-4 mt-5"
          >
            <thead>
              <tr>
                <th>{{ $t('diagnostics_group.list_header.variable') }}</th>
                <th>{{ $t('diagnostics_group.list_header.value') }}</th>
                <th>{{ $t('diagnostics_group.list_header.zscore') }}</th>
              </tr>
            </thead>
            <tbody v-if="filteredVariables.length === 0 || selectedVariables.length === 0">
              <tr>
                <td
                  colspan="4"
                  class="text-center"
                >
                  {{ $t('data_empty.no_selected_variables') }}
                </td>
              </tr>
            </tbody>
            <tbody v-else>
              <tr
                v-for="(variable, index) in selectedVariables"
                :key="index"
              >
                <td>
                  {{ variable.var_name }} - {{ variable.var_id }}
                </td>
                <td
                  style="width: 15%"
                >
                  {{ getValueForVariable(variable.var_id) }} {{ variable.unit }}
                </td>
                <td style="width: 15%">
                  {{ getValueForVariable(variable.var_id, 'z_score') }}
                </td>
              </tr>
            </tbody>
          </v-simple-table>
        </v-col>
      </v-row>
    </v-card-text>
  </v-card>
</template>
<script>

import RadarChart from '@/components/charts/RadarChart.vue'
import { getColorForValue } from '@/utils/helpers'
import LineChart from '@/components/charts/LineChart.vue'

export default {
  name: 'PlayerDiagnosticGroupWidget',
  components: {
    LineChart,
    RadarChart
  },
  props: {
    variables: {
      type: Array,
      required: true
    },
    capability: {
      type: Object,
      required: true
    }
  },
  data: () => ({
    selectedVariables: [],
    view: 'visual',
    activeChart: 'spider'
  }),
  computed: {
    filteredVariables() {
      return this.variables.filter(v => v.capability_id === this.capability.capability_id)
    }
  },
  watch: {
    selectedVariables() {
      if (this.view === 'list') return
      this.view = this.selectedVariables.length < 3 ? 'list' : 'visual'
    }
  },
  mounted() {
    this.selectedVariables = this.filteredVariables.slice(0, 5)
  },
  methods: {
    getColorFromValue(value, max, reverse = false) {
      return getColorForValue(value, max, reverse)
    },
    getValueForVariable(var_id, extract_value = 'value') {
      return this.filteredVariables.find(v => v.var_id === var_id)[extract_value]
    }
  }
}
</script>
